import { Link } from 'react-router-dom';
import SchemaOrg from '../../../components/base/SchemaOrg';
import { useEffect } from 'react';

export default function PipeCleaningPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqItems = [
    {
      question: '水管堵塞的常見原因有哪些？',
      answer: '常見原因包括：廚房油脂堆積、毛髮堵塞、食物殘渣、衛生紙或異物掉入、管線老化、樹根入侵等。不同原因需要不同的處理方式，我們會先診斷問題再對症下藥。'
    },
    {
      question: '高壓水刀會傷害管線嗎？',
      answer: '不會。我們使用的高壓水刀設備壓力可調，會根據管線材質和狀況調整適當壓力。這種物理疏通方式不使用強酸強鹼，不會腐蝕管線，反而能延長管線壽命。'
    },
    {
      question: '疏通需要多久時間？',
      answer: '一般住宅水管疏通約1-2小時，視堵塞程度而定。嚴重堵塞或需要內視鏡檢查可能需要3-4小時。我們會在現場評估後告知預計時間。'
    },
    {
      question: '疏通後會復發嗎？',
      answer: '如果是單純堵塞，疏通後正常使用不會立即復發。但如果是管線老化或結構問題，可能需要定期維護。我們會提供預防建議，並可安排定期保養服務。'
    },
    {
      question: '水管疏通費用如何計算？',
      answer: '費用依管線長度、堵塞程度、作業難度而定。一般住宅疏通約$15,000起，實際價格需現場評估。我們提供免費到府估價，報價透明，不會額外加價。'
    },
    {
      question: '你們提供緊急服務嗎？',
      answer: '是的，我們提供24小時緊急服務。如果遇到嚴重堵塞影響生活，請立即撥打緊急專線0906-629-777，我們會盡快安排師傅到府處理。'
    }
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${siteUrl}/services/pipe-cleaning#service`,
      name: '水管疏通服務',
      description: '專業水管疏通服務，使用高壓水刀技術，不傷管道快速疏通。24小時緊急服務，快速解決堵塞問題。',
      provider: {
        '@type': 'LocalBusiness',
        name: '永業環保',
        telephone: '0800-281-281',
        url: siteUrl
      },
      areaServed: [
        { '@type': 'City', name: '桃園市' },
        { '@type': 'City', name: '新北市' },
        { '@type': 'City', name: '台北市' },
        { '@type': 'City', name: '新竹市' },
        { '@type': 'City', name: '苗栗縣' }
      ],
      offers: {
        '@type': 'Offer',
        price: '15000',
        priceCurrency: 'TWD'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      '@id': `${siteUrl}/services/pipe-cleaning#faqpage`,
      mainEntity: faqItems.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${siteUrl}/services/pipe-cleaning#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首頁', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: '服務項目', item: `${siteUrl}/services` },
        { '@type': 'ListItem', position: 3, name: '水管水刀疏通', item: `${siteUrl}/services/pipe-cleaning` }
      ]
    }
  ];

  useEffect(() => {
    const pageTitle = '水管水刀疏通服務｜高壓疏通技術｜永業環保';
    const pageDesc = '永業環保提供專業水管水刀疏通服務，採用高壓水刀技術，快速疏通各類管線堵塞。24小時緊急服務，不傷管線，環保無污染，服務桃園、新北、台北、新竹、苗栗地區。';
    const pageKeywords = '水管疏通,高壓水刀,水管堵塞,管道疏通,緊急疏通,桃園水管';
    const pageUrl = `${siteUrl}/services/pipe-cleaning`;

    document.title = pageTitle;

    const setMeta = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    setMeta('meta[name="description"]', 'content', pageDesc);
    setMeta('meta[name="keywords"]', 'content', pageKeywords);
    setMeta('link[rel="canonical"]', 'href', pageUrl);
    setMeta('meta[property="og:title"]', 'content', pageTitle);
    setMeta('meta[property="og:description"]', 'content', pageDesc);
    setMeta('meta[property="og:url"]', 'content', pageUrl);
    setMeta('meta[name="twitter:title"]', 'content', pageTitle);
    setMeta('meta[name="twitter:description"]', 'content', pageDesc);

    window.scrollTo(0, 0);
  }, [siteUrl]);

  const features = [
    {
      icon: 'ri-flashlight-line',
      title: '高壓水刀技術',
      description: '使用高達 3000PSI 的高壓水流，強力沖刷管壁污垢，徹底清除堵塞物'
    },
    {
      icon: 'ri-leaf-line',
      title: '環保無污染',
      description: '純物理清潔方式，不使用化學藥劑，對環境友善且不傷害管線'
    },
    {
      icon: 'ri-time-line',
      title: '快速有效',
      description: '相較傳統通管方式，水刀疏通速度快 3 倍，當天即可恢復正常使用'
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全可靠',
      description: '專業技師操作，配備完整安全設備，確保施工過程安全無虞'
    }
  ];

  const principles = [
    {
      step: '1',
      title: '高壓水流產生',
      description: '專業設備將水加壓至 2000-3000PSI，形成強力水流'
    },
    {
      step: '2',
      title: '精準噴射清洗',
      description: '水刀噴頭深入管線，360度旋轉噴射，清除管壁附著物'
    },
    {
      step: '3',
      title: '沖刷帶出污垢',
      description: '高壓水流將油脂、毛髮、雜物等堵塞物完全沖刷帶出'
    },
    {
      step: '4',
      title: '管線恢復暢通',
      description: '徹底清潔後，管線恢復原有流通能力，排水順暢'
    }
  ];

  const scenarios = [
    {
      icon: 'ri-home-line',
      title: '住宅管線',
      items: ['廚房排水管堵塞', '浴室排水不順', '馬桶阻塞', '陽台排水管', '洗衣機排水管']
    },
    {
      icon: 'ri-restaurant-line',
      title: '餐飲業',
      items: ['廚房油脂管線', '洗碗槽排水', '截油槽清理', '地板排水溝', '污水管線']
    },
    {
      icon: 'ri-building-line',
      title: '商業大樓',
      items: ['大樓主管線', '地下室排水', '停車場排水', '公共廁所', '雨水排水系統']
    },
    {
      icon: 'ri-store-line',
      title: '工廠廠房',
      items: ['生產線排水', '廢水處理管', '化糞池管線', '雨水溝渠', '污水處理系統']
    }
  ];

  const advantages = [
    {
      title: '不傷管線',
      description: '水刀清洗不會刮傷或損壞管壁，延長管線使用壽命',
      icon: 'ri-shield-line'
    },
    {
      title: '清潔徹底',
      description: '360度旋轉噴射，清除管壁所有附著物，效果持久',
      icon: 'ri-checkbox-circle-line'
    },
    {
      title: '環保安全',
      description: '不使用化學藥劑，對環境無害，施工人員安全有保障',
      icon: 'ri-plant-line'
    },
    {
      title: '適用範圍廣',
      description: '各種材質管線皆可使用，從家用到工業管線都適用',
      icon: 'ri-global-line'
    },
    {
      title: '預防保養',
      description: '定期清洗可預防堵塞，避免緊急狀況發生',
      icon: 'ri-calendar-check-line'
    },
    {
      title: '經濟實惠',
      description: '一次徹底清潔，效果持久，長期來看更省錢',
      icon: 'ri-money-dollar-circle-line'
    }
  ];

  const faqs = [
    {
      question: '水刀疏通和傳統通管有什麼不同？',
      answer: '傳統通管多使用鋼條或化學藥劑，只能暫時疏通堵塞點，且可能傷害管線。水刀疏通使用高壓水流，能徹底清除管壁附著的油脂、污垢，清潔效果更持久，且不會損傷管線，是更環保、更有效的選擇。'
    },
    {
      question: '水刀疏通會不會把管線沖破？',
      answer: '不會的！我們的專業技師會根據管線材質、年份、狀況調整水壓強度。一般住宅管線使用 1500-2000PSI，工業管線才會使用到 3000PSI。而且水刀是沖刷污垢，不是撞擊管壁，所以不會損傷管線。'
    },
    {
      question: '疏通需要多少時間？',
      answer: '一般住宅管線堵塞，疏通時間約 30 分鐘到 1 小時。如果是嚴重堵塞或長距離管線，可能需要 2-3 小時。我們會在現場評估後告知預估時間，並盡快完成作業。'
    },
    {
      question: '什麼情況需要使用水刀疏通？',
      answer: '當您發現排水速度變慢、經常堵塞、有異味、或傳統通管方式無效時，就建議使用水刀疏通。特別是廚房油脂管線、餐廳排水系統，定期使用水刀清洗可以預防堵塞問題。'
    },
    {
      question: '水刀疏通的費用如何計算？',
      answer: '費用依管線長度、堵塞程度、施工難度而定。一般出車費用為15000 元起，整棟管線清洗則需現場評估報價。我們提供現場勘查服務，報價透明公開，不會有額外收費。'
    },
    {
      question: '疏通後需要多久清洗一次？',
      answer: '建議住宅每 1-2 年定期清洗一次，餐飲業因油脂較多，建議每 3-6 個月清洗。定期保養可以預防堵塞，延長管線壽命，長期來看更經濟實惠。我們也提供定期保養合約服務。'
    }
  ];

  const relatedServices = [
    {
      title: '內視鏡檢查',
      description: '精密檢測管線內部狀況，找出問題根源',
      icon: 'ri-search-eye-line',
      link: '/services/endoscope'
    },
    {
      title: '水溝清洗',
      description: '專業清理排水溝渠，預防淹水問題',
      icon: 'ri-contrast-drop-line',
      link: '/services/drain-cleaning'
    },
    {
      title: '水塔清洗',
      description: '定期清洗水塔，確保飲用水安全',
      icon: 'ri-drop-line',
      link: '/services/water-tank'
    }
  ];

  return (
    <>
      <SchemaOrg schema={schemas} />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-cyan-50 via-white to-teal-50 pt-32 pb-20 md:pt-32 md:pb-20 py-20">
          <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/cd31e24d907be6b21f0d682e55982ae2.jpg')] bg-cover bg-center opacity-5"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-water-flash-line"></i>
                <span>高壓水刀專業技術</span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 font-serif">
                水管水刀疏通服務
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                採用先進高壓水刀技術，快速疏通各種管線堵塞<br className="hidden md:block" />
                環保無污染，不傷管線，效果持久，當天恢復正常使用
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-medium hover:bg-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>緊急疏通專線</span>
                </Link>
                <a
                  href="tel:0800-281-281"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-50 transition-all duration-300 border-2 border-cyan-600 whitespace-nowrap"
                >
                  <i className="ri-customer-service-line text-lg"></i>
                  <span>0800-281-281</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  水刀疏通四大優勢
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  為什麼選擇高壓水刀疏通？
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-cyan-50 to-white p-8 rounded-xl border border-cyan-100 hover:border-cyan-300 hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${feature.icon} text-3xl text-white`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  高壓水刀工作原理
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  了解水刀如何徹底清潔您的管線
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=High%20pressure%20water%20jet%20nozzle%20spraying%20powerful%20water%20stream%20inside%20pipe%2C%20cross%20section%20view%20of%20pipe%20cleaning%20process%2C%20water%20droplets%20and%20spray%20effect%2C%20professional%20equipment%20close%20up%2C%20clean%20and%20modern%20style%2C%20technical%20illustration%20quality%2C%20bright%20lighting&width=600&height=600&seq=water-jet-principle&orientation=squarish"
                    alt="高壓水刀原理"
                    className="w-full h-full object-cover rounded-2xl shadow-2xl"
                  />
                </div>
                <div className="space-y-6">
                  {principles.map((principle, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {principle.step}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {principle.title}
                        </h3>
                        <p className="text-gray-600">
                          {principle.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gradient-to-br from-cyan-50 to-teal-50 p-6 md:p-8 rounded-2xl border border-cyan-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      專業提醒
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      高壓水刀疏通需要專業設備與技術，<strong>請勿自行操作</strong>。不當使用可能造成管線損壞或人員受傷。永業環保擁有 20 年專業經驗，配備完整安全設備，讓您安心託付。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Scenarios */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  適用場景
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  各種管線堵塞問題，我們都能解決
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {scenarios.map((scenario, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-100 hover:border-cyan-200 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center mb-4">
                      <i className={`${scenario.icon} text-2xl text-cyan-600`}></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4">
                      {scenario.title}
                    </h3>
                    <ul className="space-y-2">
                      {scenario.items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <i className="ri-check-line text-cyan-600"></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Advantages */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-cyan-50 via-white to-teal-50">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  選擇水刀疏通的理由
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  相較傳統通管方式，水刀疏通更勝一籌
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {advantages.map((advantage, index) => (
                  <div
                    key={index}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                        <i className={`${advantage.icon} text-xl text-cyan-600`}></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {advantage.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {advantage.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  常見問題
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  關於水刀疏通的疑問，這裡都有解答
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <details
                    key={index}
                    className="bg-gradient-to-br from-gray-50 to-white p-4 md:p-6 rounded-xl border border-gray-100 hover:border-cyan-200 transition-all duration-300 group"
                  >
                    <summary className="flex items-center justify-between cursor-pointer list-none">
                      <h3 className="text-base md:text-lg font-bold text-gray-900 pr-4">
                        {faq.question}
                      </h3>
                      <i className="ri-arrow-down-s-line text-2xl text-cyan-600 group-open:rotate-180 transition-transform duration-300 flex-shrink-0"></i>
                    </summary>
                    <p className="mt-4 text-sm md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
                  相關服務
                </h2>
                <p className="text-base md:text-lg text-gray-600">
                  更多專業服務，滿足您的需求
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.link}
                    className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 group"
                  >
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-3xl text-cyan-600`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center gap-2 text-cyan-600 font-medium">
                      <span>了解更多</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-2 transition-transform duration-300"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 md:py-20 bg-gradient-to-br from-cyan-600 to-teal-600 relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20flowing%20water%20pattern%20with%20dynamic%20waves%20and%20splashes%2C%20minimalist%20design%2C%20soft%20gradient%20background%2C%20powerful%20and%20clean%20atmosphere%2C%20modern%20abstract%20style%2C%20high%20quality%20digital%20art%2C%20cyan%20and%20teal%20color%20scheme%2C%20water%20jet%20effect&width=1920&height=400&seq=pipe-cleaning-cta-bg&orientation=landscape')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif">
                管線堵塞不用慌，水刀疏通快速搞定
              </h2>
              <p className="text-lg md:text-xl mb-8 opacity-90">
                24 小時緊急服務，接到電話立即出發<br className="hidden md:block" />
                專業團隊、先進設備，讓您的管線恢復暢通
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-4">
                <a
                  href="tel:0800-281-281"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-medium hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-calendar-check-line text-lg"></i>
                  <span>立即預約</span>
                </a>
                <a
                  href="tel:0906629777"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-transparent text-white px-8 py-4 rounded-lg font-medium hover:bg-white/10 transition-all duration-300 border-2 border-white whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-lg"></i>
                  <span>緊急專線</span>
                </a>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
